import { useCallback, useMemo, useState } from 'react';
import { useStore, IS_STAGING, IS_DEV_ENV, DEMO_MODE } from '../store/StoreContext.jsx';
import {
  getAssessmentDefaults,
  getAssessmentAssumptions,
  buildIncomeTimeline,
  getGapSummary,
  getTaxPoolSnapshot,
  getHoldingsBundle,
  analyzeFees,
  getRmdOutlook,
  analyzeTaxLocation,
  getSituationSummary,
  getProtectionChecklist,
  getPlanQuestions,
  getStyleBox,
  getRothConversionOutlook,
  compareFeeAlternatives,
  getGoalSheet,
  getHorizon,
  summarizeUnpricedHoldings,
} from '../store/assessmentSelectors.js';
import { getDashboardMetrics } from '../store/selectors.js';
import usePortfolioStats from '../store/usePortfolioStats.js';
import useMeeting from '../store/useMeeting.js';
import { SECTIONS, meetingIncludes, storeKeyFor } from '../store/meetingSections.js';
import MeetingPanel from '../components/assessment/MeetingPanel.jsx';
import SendPanel from '../components/assessment/SendPanel.jsx';
import MissingInputs from '../components/assessment/MissingInputs.jsx';
import useSendPreview from '../store/useSendPreview.js';
import { sendIncludes, sendDigest, sendLimitations, periodLabel } from '../store/sendPreview.js';
import useAssessmentSync from '../store/useAssessmentSync.js';
import { describeSync } from '../store/assessmentSync.js';
import { money } from '../components/assessment/format.js';
import GoalsSection from '../components/assessment/GoalsSection.jsx';
import TaxPoolsSection from '../components/assessment/TaxPoolsSection.jsx';
import IncomeGapSection from '../components/assessment/IncomeGapSection.jsx';
import FeeXraySection from '../components/assessment/FeeXraySection.jsx';
import SituationSummary from '../components/assessment/SituationSummary.jsx';
import GoalSheet from '../components/assessment/GoalSheet.jsx';
import WhiteboardSection from '../components/assessment/WhiteboardSection.jsx';
import CashFlowGuide from '../components/assessment/CashFlowGuide.jsx';
import { getCashFlowTimeline } from '../store/cashFlowTimeline.js';
import AreaHeader from '../components/assessment/AreaHeader.jsx';
import ProtectionSection from '../components/assessment/ProtectionSection.jsx';
import NextStepsSection from '../components/assessment/NextStepsSection.jsx';
import TaxHorizonSection from '../components/assessment/TaxHorizonSection.jsx';
import LookThroughSection from '../components/assessment/LookThroughSection.jsx';
import StyleBox from '../components/assessment/StyleBox.jsx';
import BehaviourSection from '../components/assessment/BehaviourSection.jsx';
import ModelComparisonStub from '../components/assessment/ModelComparisonStub.jsx';
import AssessmentDisclosure from '../components/assessment/AssessmentDisclosure.jsx';
import sampleData from '../fixtures/sampleAssessment.json';

// Holdings fixtures keyed to a household, used when a live analysis has not been
// run. Populated for the demo household once the YCharts pull is frozen; empty
// otherwise so nothing invented is ever attributed to a real client.
const FIXTURES_BY_LEAD = {};

const urlFlag = name => {
  try {
    const raw = new URLSearchParams(window.location.search).get(name);
    if (raw === null) return false;
    return !['0', 'false', 'no', 'off'].includes(raw.toLowerCase());
  } catch {
    return false;
  }
};

// The household the assessment belongs to. The endpoint that saves it is keyed
// by Lead, so a household-only launch (no lead) is not saved to Salesforce yet.
const leadIdFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get('leadId') || params.get('leadid') || '').trim();
  } catch {
    return '';
  }
};

const fmtDate = d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export default function AssessmentScreen({ onBack }) {
  const { data, update, readOnly } = useStore();

  // The sample household is explicit opt-in (or the anonymous demo build), so a
  // planner who lands on /assessment by accident never sees someone else's
  // numbers, and the demo never needs a Salesforce record.
  const useSample = urlFlag('sample') || DEMO_MODE;
  const source = useSample ? sampleData.sampleStore : data;

  // The analysis and the sketches are saved to Salesforce as well as kept in
  // the browser. Never for the sample household, which has no record.
  const { sync, markChanged } = useAssessmentSync({
    leadId: leadIdFromUrl(),
    enabled: !useSample,
    readOnly,
    assessment: data?.assessment,
    update,
  });

  // The advisor's choices live in one store key that is saved to Salesforce as
  // its own part, so a planner's preparation reaches the advisor's machine.
  const setMeta = useCallback((path, value) => {
    update(`assessment.meta.${path}`, value);
    markChanged('meta');
  }, [update, markChanged]);

  const saveSketch = useCallback(id => dataUrl => {
    update(`assessment.sketches.${id}`, dataUrl);
    markChanged('sketches');
  }, [update, markChanged]);

  const setStatus = useCallback(({ status, message }) => {
    update('assessment.analysis', { status, message });
  }, [update]);

  const setBundle = useCallback(bundle => {
    update('assessment.holdings', bundle);
    markChanged('analysis');
  }, [update, markChanged]);

  const view = useMemo(() => {
    // What-if state always lives on the real store, even in sample mode, so the
    // sliders work identically in a demo.
    const withScenario = useSample
      ? { ...source, assessment: data?.assessment }
      : source;

    const metrics = getDashboardMetrics(withScenario);
    const defaults = getAssessmentDefaults(withScenario);
    const assumptions = getAssessmentAssumptions(withScenario);
    const timeline = buildIncomeTimeline(withScenario, assumptions);
    const horizon = getHorizon(withScenario, timeline);
    const taxPools = getTaxPoolSnapshot(withScenario);
    const summary = getGapSummary(timeline, {
      client1Name: (withScenario?.family?.client1FirstName || '').trim() || null,
      liquidAssets: taxPools.total,
      assumptions,
    });
    const bundle = getHoldingsBundle(withScenario, {
      allowSample: useSample,
      fixturesByLead: FIXTURES_BY_LEAD,
      sample: sampleData.bundle,
    });

    const fam = withScenario?.family || {};
    const first = (fam.client1FirstName || '').trim();
    const second = (fam.client2FirstName || '').trim();
    const last = (fam.client1LastName || '').trim();

    const fees = analyzeFees(bundle);
    const rmd = getRmdOutlook(withScenario);
    const location = analyzeTaxLocation(bundle);
    const lookThrough = bundle?.lookThrough || null;
    const feeComparison = compareFeeAlternatives(bundle);
    const styleBox = getStyleBox(bundle);
    // The advisor may read section 2 from the bridge years or from the steady
    // state; the choice is kept with the assessment.
    const goalSheet = getGoalSheet(withScenario, { assumptions, timeline, taxPools, period: data?.assessment?.meta?.meeting?.period });
    // The ruling under the cash-flow whiteboard, read off the same timeline as
    // the goal sheet so the board and the sheet can never disagree.
    const cashFlow = getCashFlowTimeline(withScenario, { assumptions, timeline, taxPools });
    // The window, not a conversion. This used to illustrate moving the ENTIRE
    // tax-deferred balance out before withdrawals begin, evenly across the
    // remaining years, and present the resulting figures beside the current
    // ones. Nobody chose that amount: it is whatever the balance divided by
    // the window happens to be, it asserts affordability and a tax cost the
    // questionnaire cannot know, and at a seven-figure balance it is not a
    // conversion any planner would propose. The size of the lever is the pool
    // and the years, both of which are facts; what to convert is the work of
    // the plan.
    const roth = getRothConversionOutlook(rmd, { annual: 0, years: 0 });

    return {
      metrics,
      defaults,
      assumptions,
      timeline,
      taxPools,
      summary,
      bundle,
      fees,
      // Reads the questionnaire, so it stands up with no statement uploaded.
      rmd,
      roth,
      // Reads the statements, so it stays null until one has been analyzed.
      location,
      lookThrough,
      feeComparison,
      styleBox,
      goalSheet,
      cashFlow,
      horizon,
      protection: getProtectionChecklist(withScenario),
      planQuestions: getPlanQuestions(withScenario, {
        goalSheet,
        rmd,
        roth,
        taxPools,
        protection: getProtectionChecklist(withScenario),
      }),
      // Computed last, because it summarizes what everything above produced.
      situation: getSituationSummary({
        taxPools, summary, rmd, location, fees, lookThrough, assumptions, feeComparison, roth,
      }),
      // Positions with a value but no price. Null when there are none, so the
      // page says nothing rather than showing an empty warning.
      unpriced: summarizeUnpricedHoldings(bundle),
      names: {
        client1: first || 'You',
        client2: second || 'Your spouse',
        joint: [first, second].filter(Boolean).join(' and '),
        display: [[first, second].filter(Boolean).join(' and '), last].filter(Boolean).join(' '),
      },
      hasClient: Boolean(first || last),
      hasAssets: taxPools.total > 0 || metrics.totalAssets > 0,
    };
  }, [source, data?.assessment, useSample]);

  // The figures YCharts is still calculating arrive separately. Polling lives
  // in the browser because the calculation outlives any proxied request.
  const portfolioPoll = usePortfolioStats(view.bundle?.portfolio?.calc);
  // The meeting is the sections chosen for it, in page order; the advisor's
  // choices for this household override the default list.
  const meetingInclude = useMemo(
    () => meetingIncludes(data?.assessment?.meta?.meeting?.include),
    [data?.assessment?.meta?.meeting?.include]
  );
  const present = useMeeting({ include: meetingInclude });

  // The send version: the packet as it will print, reviewed before it can be.
  const [sending, setSending] = useState(false);
  const sendInclude = useMemo(
    () => sendIncludes(data?.assessment?.meta?.send?.include),
    [data?.assessment?.meta?.send?.include]
  );
  useSendPreview({ sending: sending && !readOnly, include: sendInclude });
  const meeting = useMemo(() => ({
    sections: SECTIONS.map(s => ({ ...s, on: meetingInclude.has(s.id), available: present.available.includes(s.id) })),
    count: SECTIONS.filter(s => meetingInclude.has(s.id) && present.available.includes(s.id)).length,
    onToggle: (id, on) => setMeta(`meeting.include.${storeKeyFor(id)}`, on),
  }), [meetingInclude, present.available, setMeta]);

  const asOf = fmtDate(new Date());

  // What the questionnaire has not been given yet, in the order the report
  // reads from it. Named so a zero below is understood as not entered.
  const missing = useMemo(() => {
    const out = [];
    if (!view.hasAssets) out.push({ key: 'assets', text: 'Assets: no investment, retirement or bank accounts have been entered. The tax pools, goals and the coverage figures all read from them.' });
    if (!(view.timeline?.[0]?.totalIncome > 0)) out.push({ key: 'income', text: 'Income: no salary, Social Security or pension has been entered, so every year shows nothing coming in.' });
    if (!(view.assumptions?.annualSpending > 0)) out.push({ key: 'spending', text: 'Spending: living expenses have not been entered, so there is nothing to measure income against.' });
    return out;
  }, [view]);

  // A real household with nothing entered yet gets an honest empty state rather
  // than a report full of zeroes with their name on it.
  // Only a household with nobody in it gets the empty page. One with a name
  // and nothing else gets the report, with what is missing named at the top.
  if (!useSample && !view.hasClient) {
    return (
      <div className="assessment-screen">
        <Toolbar onBack={onBack} readOnly={readOnly} sync={sync} />
        <div className="asmt-doc">
          <section className="asmt-card asmt-empty">
            <h2>There is no questionnaire data for this household yet</h2>
            <p>
              The assessment is built from the questionnaire, so it needs the
              client's assets and income before it can show anything.
            </p>
            <button type="button" className="asmt-btn" onClick={onBack}>
              Open the questionnaire
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={`assessment-screen${present.presenting ? ' is-presenting' : ''}${sending && !readOnly ? ' is-sending' : ''}`}>
      <Toolbar onBack={onBack} readOnly={readOnly} sample={useSample} onPresent={present.start} sync={sync} meeting={meeting} onSend={readOnly ? null : () => setSending(true)} />

      {/* Print-only cover, reusing the presented sheet's cover styling so the
          two client-facing artefacts arrive looking like the same firm. */}
      <div className="pres-cover" aria-hidden="true">
        <div className="pres-cover-band" />
        <div className="pres-cover-body">
          <div className="pres-cover-top">
            <div className="pres-cover-firm">Pure Financial Advisors</div>
            <div className="pres-cover-rule" />
            <div className="pres-cover-title">Financial Assessment</div>
            {view.names.display && <div className="pres-cover-client">{view.names.display}</div>}
            <div className="pres-cover-date">{asOf}</div>
          </div>
          <div className="pres-cover-foot">
            <img src="/pure-logo-light.svg" alt="" className="pres-cover-logo" />
            <div className="pres-cover-tag">Trustworthy by Design<sup>SM</sup></div>
          </div>
        </div>
      </div>

      {sending && !readOnly && (() => {
        const sketches = data?.assessment?.sketches || {};
        const digest = sendDigest({ sections: sendInclude, period: view.goalSheet?.income?.period, view, sketches });
        const reviewed = data?.assessment?.meta?.send?.reviewed || null;
        const limitations = sendLimitations({ view, sketches });
        const drawn = k => { const b = sketches[k]; return typeof b === 'string' || Boolean(b && ((b.strokes && b.strokes.length) || b.legacy)); };
        return (
          <SendPanel
            prepared={asOf}
            statements={view.bundle ? `${view.bundle.source || 'Analyzed'}${view.bundle.asOf ? `, as of ${view.bundle.asOf}` : ''}` : null}
            period={periodLabel(view.goalSheet?.income)}
            sections={SECTIONS.map(s => ({ ...s, on: sendInclude.has(s.id), available: present.available.includes(s.id) }))}
            onToggle={(id, on) => setMeta(`send.include.${storeKeyFor(id)}`, on)}
            boards={[{ id: 'cashflow', title: 'The cash flow, drawn', drawn: drawn('cashflow') }, { id: 'tax', title: 'The tax picture, drawn', drawn: drawn('tax') }]}
            limitations={limitations}
            reviewed={reviewed}
            reviewedCurrent={Boolean(reviewed && reviewed.digest === digest)}
            canReview={!limitations.some(l => l.blocking) && !(reviewed && reviewed.digest === digest)}
            onReview={() => setMeta('send.reviewed', { at: new Date().toISOString(), digest, sections: [...sendInclude], period: view.goalSheet?.income?.period || null })}
            onPrint={() => window.print()}
            onBack={() => setSending(false)}
          />
        );
      })()}

      <div className="asmt-doc">
        {!useSample && <MissingInputs items={missing} onBack={onBack} />}
        <section className="asmt-card asmt-hero" id="opening">
          <h1>{view.names.joint ? `${view.names.joint}, here is where you stand` : 'Your financial assessment'}</h1>
          <p className="asmt-hero-sub">Prepared {asOf} from the questionnaire you completed with your advisor.</p>
          <div className="asmt-kpis">
            <div>
              <div className="asmt-kpi-label">Net worth</div>
              <div className="asmt-kpi-value">{money(view.metrics.totalNetWorth)}</div>
            </div>
            <div>
              <div className="asmt-kpi-label">Liquid savings</div>
              <div className="asmt-kpi-value">{money(view.taxPools.total)}</div>
            </div>
            <div>
              <div className="asmt-kpi-label">Income you can count on</div>
              <div className="asmt-kpi-value">{money(view.timeline[0]?.totalIncome || 0)}</div>
            </div>
          </div>

          {/* Inside the opening, not after it: the meeting starts on the
              numbers AND the client's own words, not on a title card that has
              to be clicked past. */}
          <GoalsSection goals={source?.goals?.clientGoals} />
        </section>

        <SituationSummary situation={view.situation} />

        <GoalSheet sheet={view.goalSheet} onPeriod={readOnly ? null : p => setMeta('meeting.period', p)} />

        {/* From here the report walks the goal sheet's five areas in order,
            which is the flow the advisor runs the meeting to. */}
        <AreaHeader n="1" title="Preservation and Conservative Growth of Principal" />

        <TaxPoolsSection snapshot={view.taxPools} bundle={view.bundle} />

        <AreaHeader n="2" title="Retirement Income and Cash Flow Strategy" />

        <IncomeGapSection timeline={view.timeline} summary={view.summary} horizon={view.horizon} />

        <WhiteboardSection
          id="board-cashflow"
          title="The cash flow, drawn"
          guideModel={view.cashFlow}
          renderGuide={model => <CashFlowGuide model={model} />}
          saved={data?.assessment?.sketches?.cashflow}
          onSave={saveSketch('cashflow')}
          readOnly={readOnly}
        />

        <AreaHeader n="3" title="Reduce Taxes" />

        <TaxHorizonSection
          rmd={view.rmd}
          roth={view.roth}
          location={view.location}
          names={view.names}
        />

        <WhiteboardSection
          id="board-tax"
          title="The tax picture, drawn"
          saved={data?.assessment?.sketches?.tax}
          onSave={saveSketch('tax')}
          readOnly={readOnly}
        />

        <AreaHeader n="4" title="Efficient Management of Assets" />

        <LookThroughSection lookThrough={view.lookThrough} names={view.names} />

        <StyleBox box={view.styleBox} />

        <BehaviourSection calc={view.bundle?.portfolio?.calc} poll={portfolioPoll} />

        <FeeXraySection
          fees={view.fees}
          comparison={view.feeComparison}
          bundle={view.bundle}
          unpriced={view.unpriced}
          analysis={data?.assessment?.analysis}
          onStatus={setStatus}
          onBundle={setBundle}
          clientName={view.names.display}
          readOnly={readOnly}
        />

        <AreaHeader n="5" title="Estate and Asset Preservation" />

        <ProtectionSection checklist={view.protection} />

        <NextStepsSection questions={view.planQuestions} />

        <ModelComparisonStub
          show={urlFlag('model') || Boolean(data?.assessment?.ui?.showModelComparison)}
          bundle={view.bundle}
        />

        <AssessmentDisclosure bundle={view.bundle} />
      </div>

      {present.presenting && (
        <div className="asmt-present-chrome no-print">
          <button type="button" className="asmt-btn is-ghost" onClick={() => present.step(-1)}>
            &larr; Back
          </button>
          <div className="asmt-present-where">
            <span className="asmt-present-count">{present.context.n} / {present.context.count}</span>
            {present.context.area && <span className="asmt-present-area">{present.context.area}</span>}
          </div>
          <button type="button" className="asmt-btn" onClick={() => present.step(1)}>
            Next &rarr;
          </button>
          <button type="button" className="asmt-btn is-ghost" onClick={present.stop}>
            Exit
          </button>
        </div>
      )}
      
    </div>
  );
}

function Toolbar({ onBack, readOnly, sample, onPresent, sync, meeting, onSend }) {
  const status = describeSync(sync);
  return (
    <div className="asmt-toolbar no-print">
      <div className="asmt-toolbar-title">Financial Assessment</div>
      {sample && <span className="asmt-pill">Sample</span>}
      {IS_STAGING && <span className="asmt-pill is-staging">Staging</span>}
      {IS_DEV_ENV && <span className="asmt-pill is-staging">Dev</span>}
      {readOnly && <span className="asmt-pill is-view">View only</span>}
      {status && (
        <span className={`asmt-sync is-${status.tone}`} role="status" aria-live="polite">
          {status.text}
        </span>
      )}
      <button type="button" className="asmt-btn is-ghost" onClick={onBack}>
        Back to questionnaire
      </button>
      {onPresent && !readOnly && meeting && (
        <MeetingPanel sections={meeting.sections} count={meeting.count} onToggle={meeting.onToggle} />
      )}
      {onPresent && !readOnly && (
        <button type="button" className="asmt-btn" onClick={onPresent}>
          Present
        </button>
      )}
      {onSend ? (
        <button type="button" className="asmt-btn" onClick={onSend}>
          Send preview
        </button>
      ) : (
        <button type="button" className="asmt-btn" onClick={() => window.print()}>
          Print / Save PDF
        </button>
      )}
    </div>
  );
}
